/**
 * UX- en copy-profiel per klanttype-id (bron van waarheid voor gedrag in dossier).
 */
import type { CustomerTypeId } from './customer-types';
import { CUSTOMER_TYPE_OPTIONS } from './customer-types';

export interface CustomerUxProfile {
  /** Korte zin na "Label: " in journey-profielhint */
  customerInsight: string;
  /** Paragraaf onder resultaat-samenvatting */
  resultaatSamenvattingTone: string;
  /** simple: minder technische tussenlagen; full: alles zichtbaar */
  resultDetailLevel: 'simple' | 'standard' | 'full';
  showCo2InResultaat: boolean;
  /** Materialen/arbeid/materieel-strook per regel in Resultaat */
  showPerLineCostSplit: boolean;
  /** Waarschuwing in Posten als veel regels zonder bouwcluster */
  emphasizeClusterInPosten: boolean;
  /** Extra regel in exportmodal over modelcorrectie */
  rapportageShowModelCorrectionHint: boolean;
}

const DEFAULT_PROFILE: CustomerUxProfile = {
  customerInsight: 'professionele standaardopzet voor dit dossier.',
  resultaatSamenvattingTone: 'Professionele modelonderbouwing voor dit dossier.',
  resultDetailLevel: 'standard',
  showCo2InResultaat: true,
  showPerLineCostSplit: true,
  emphasizeClusterInPosten: false,
  rapportageShowModelCorrectionHint: true,
};

const PROFILES: Record<string, CustomerUxProfile> = {
  particulier: {
    customerInsight: 'duidelijke uitleg en overzichtelijke kostenopbouw zonder overdaad aan detail.',
    resultaatSamenvattingTone:
      'Heldere kostenopbouw zonder jargon — geschikt om rustig na te lopen met je adviseur of aannemer.',
    resultDetailLevel: 'simple',
    showCo2InResultaat: false,
    showPerLineCostSplit: false,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: false,
  },
  aannemer: {
    customerInsight: 'snelle detailstructuur per bouwdeel voor calculatie, meerwerk en interne controle.',
    resultaatSamenvattingTone: 'Snelle onderbouwing per bouwdeel voor calculatie, meerwerk en interne controle.',
    resultDetailLevel: 'full',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: true,
    rapportageShowModelCorrectionHint: true,
  },
  werkvoorbereider: {
    customerInsight:
      'gestructureerde posten en hoeveelheden om uitvoering, inkoop en planning op elkaar af te stemmen.',
    resultaatSamenvattingTone:
      'Post- en hoeveelheidsstructuur om werkvoorbereiding, inkoop en uitvoering te ondersteunen.',
    resultDetailLevel: 'full',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: true,
    rapportageShowModelCorrectionHint: true,
  },
  projectontwikkelaar: {
    customerInsight: 'investeringsraming en vergelijkbare posten tussen faseringen en dossiers.',
    resultaatSamenvattingTone: 'Vergelijkbare poststructuur voor investeringsraming en fasering tussen dossiers.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: true,
  },
  vastgoedontwikkelaar: {
    customerInsight: 'investeringsraming en vergelijkbare posten tussen faseringen en dossiers.',
    resultaatSamenvattingTone: 'Vergelijkbare poststructuur voor investeringsraming en fasering tussen dossiers.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: true,
  },
  woningcorporatie: {
    customerInsight: 'transparante posten en herhaalbare uitvraag voor portefeuille of complex.',
    resultaatSamenvattingTone: 'Transparante posten voor portefeuille, huurderscommunicatie en interne besluitvorming.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: true,
    rapportageShowModelCorrectionHint: true,
  },
  vve: {
    customerInsight: 'transparante posten en herhaalbare uitvraag voor portefeuille of complex.',
    resultaatSamenvattingTone: 'Transparante posten voor VvE-vergadering, vergelijking en besluitvorming.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: true,
    rapportageShowModelCorrectionHint: true,
  },
  huizenflipper: {
    customerInsight: 'snelle renovatieposten en rendementsinzicht.',
    resultaatSamenvattingTone: 'Snel inzicht in renovatie- en verbouwposten voor rendementsberekening.',
    resultDetailLevel: 'simple',
    showCo2InResultaat: false,
    showPerLineCostSplit: false,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: false,
  },
  overheid: {
    customerInsight: 'toetsbare onderbouwing en consistente documentatie.',
    resultaatSamenvattingTone: 'Toetsbare kostenopbouw voor dossier en interne beoordeling.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: true,
  },
  bank_financier: {
    customerInsight: 'gedocumenteerde kostenstructuur voor dossier en risicobeoordeling.',
    resultaatSamenvattingTone: 'Gedocumenteerde kostenstructuur ter ondersteuning van financieringsdossiers.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: true,
  },
  adviseur: {
    customerInsight: 'detailstructuur per bouwdeel om ontwerp en begroting op elkaar af te stemmen.',
    resultaatSamenvattingTone: 'Detailstructuur per regel om ontwerp en begroting op elkaar af te stemmen.',
    resultDetailLevel: 'full',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: true,
    rapportageShowModelCorrectionHint: true,
  },
  anders: {
    customerInsight: 'neutrale, professionele opzet; stel zelf de nadruk in overleg met je team.',
    resultaatSamenvattingTone: 'Professionele modelonderbouwing — pas detailniveau aan op je eigen werkwijze.',
    resultDetailLevel: 'standard',
    showCo2InResultaat: true,
    showPerLineCostSplit: true,
    emphasizeClusterInPosten: false,
    rapportageShowModelCorrectionHint: true,
  },
};

export function getCustomerUxProfile(customerTypeId: string): CustomerUxProfile {
  const p = PROFILES[customerTypeId];
  if (p) return p;
  const known = CUSTOMER_TYPE_OPTIONS.some((o) => o.id === customerTypeId);
  if (!known) return { ...DEFAULT_PROFILE, customerInsight: `profiel voor dit segment (${customerTypeId}).` };
  return DEFAULT_PROFILE;
}

export function isCustomerTypeId(id: string): id is CustomerTypeId {
  return CUSTOMER_TYPE_OPTIONS.some((o) => o.id === id);
}

/** Eén regel onder het analyse-resultaat (tekening). */
export function getDrawingAnalysisAudienceHint(customerTypeId: string): string {
  const p = getCustomerUxProfile(customerTypeId);
  return `Analyse afgestemd op jouw klanttype: ${p.customerInsight}`;
}
